import { useCallback, useContext, useEffect, useMemo, useReducer } from "react";
import UserContext from "common/Context/UserContext";
import { clearCustomerContextHeaders } from "common/HttpClient/CustomerContextHeaders";
import { USER_PAGE_TOAST } from "common/Constants/UserPageToast.constants";
import { useUserPageToast } from "common/hooks/UseUserPageToast";
import { initialState } from "./PlatformUserAccess.model";
import {
  PLATFORM_USER_ACCESS_ACTIONS,
  PlatformUserAccessReducer,
} from "./PlatformUserAccess.reducers";
import {
  isPlatformMutationSuccess,
  PlatformUserAccessServices,
} from "./PlatformUserAccess.services";
import {
  CUSTOMER_ACCESS_STATUS,
  DEFAULT_PLATFORM_LIST_FILTERS,
  normalizeCustomerAccessStatus,
  normalizePlatformListFilters,
  PLATFORM_USER_LIST_ACTION,
  PLATFORM_USER_ACCESS_UI,
} from "./PlatformUserAccess.constants";
import { useCustomerUserListActions } from "../Admin/Users/Users.hook";
import { reloadPageIfLoggedInUser } from "common/Utilities/SessionUser.utils";

const PLATFORM_TOAST = USER_PAGE_TOAST.platform;

export const usePlatformUserAccessInitialization = () => {
  const [state, dispatch] = useReducer(PlatformUserAccessReducer, initialState);
  const { customerRoleOptions, activeCustomersList, userProfile } = useContext(UserContext);
  const sessionProfile = userProfile?.profile;
  const services = useMemo(() => PlatformUserAccessServices(), []);
  const { toast, showSuccess, showFailure, dismissToast } = useUserPageToast();
  const { onCustomerUserListAction } = useCustomerUserListActions({
    showSuccess,
    showFailure,
  });

  const loadUsers = useCallback(
    async (
      page = state.pagination.page,
      size = state.pagination.size,
      filtersArg
    ) => {
      const filters =
        filtersArg !== undefined && filtersArg !== null
          ? filtersArg
          : state.listFilters;
      dispatch({
        type: PLATFORM_USER_ACCESS_ACTIONS.LOADING,
        payload: true,
      });
      try {
        const response = await services.getUsers({
          page,
          size,
          username: filters.username,
          email: filters.email,
          firstName: filters.firstName,
          lastName: filters.lastName,
          phone: filters.phone,
          platformRole: filters.platformRole,
          accountStatus: filters.accountStatus,
        });
        if (response?.error) {
          dispatch({
            type: PLATFORM_USER_ACCESS_ACTIONS.ERROR,
            payload: response.error,
          });
          return;
        }
        dispatch({
          type: PLATFORM_USER_ACCESS_ACTIONS.USERS,
          payload: response?.data ?? {},
        });
      } catch {
        dispatch({
          type: PLATFORM_USER_ACCESS_ACTIONS.ERROR,
          payload: "Unable to load user access list",
        });
      } finally {
        dispatch({
          type: PLATFORM_USER_ACCESS_ACTIONS.LOADING,
          payload: false,
        });
      }
    },
    [services, state.pagination.page, state.pagination.size, state.listFilters]
  );

  const checkInviteEmail = useCallback(
    async (email) => {
      const response = await services.checkEmailExists(email);
      if (response?.error) {
        return { exists: false, error: response.error };
      }
      return { exists: Boolean(response?.data?.exists), error: null };
    },
    [services]
  );

  const loadUserDetails = useCallback(
    async (userId) => {
      dispatch({
        type: PLATFORM_USER_ACCESS_ACTIONS.SELECTED_USER_ID,
        payload: userId,
      });
      const response = await services.getUserById(userId);
      if (response?.error) {
        dispatch({
          type: PLATFORM_USER_ACCESS_ACTIONS.ERROR,
          payload: response.error,
        });
        return;
      }
      dispatch({
        type: PLATFORM_USER_ACCESS_ACTIONS.SELECTED_USER_DETAILS,
        payload: response?.data ?? null,
      });
    },
    [services]
  );

  const onListFiltersChange = useCallback(
    async (rawColumnFilters) => {
      const normalized = normalizePlatformListFilters(rawColumnFilters);
      dispatch({
        type: PLATFORM_USER_ACCESS_ACTIONS.LIST_FILTERS,
        payload: normalized,
      });
      await loadUsers(0, state.pagination.size, normalized);
    },
    [loadUsers, state.pagination.size]
  );

  const onClearFilters = useCallback(async () => {
    const empty = { ...DEFAULT_PLATFORM_LIST_FILTERS };
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.LIST_FILTERS,
      payload: empty,
    });
    await loadUsers(0, state.pagination.size, empty);
  }, [loadUsers, state.pagination.size]);

  const onRowsPerPageChange = useCallback(
    async (size) => {
      await loadUsers(0, size);
    },
    [loadUsers]
  );

  const onPageChange = useCallback(
    async (nextPage) => {
      await loadUsers(nextPage, state.pagination.size);
    },
    [loadUsers, state.pagination.size]
  );

  const openInviteModal = useCallback(() => {
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.INVITE_MODAL,
      payload: true,
    });
  }, []);

  const closeInviteModal = useCallback(() => {
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.INVITE_MODAL,
      payload: false,
    });
  }, []);

  const inviteUser = useCallback(
    async (payload) => {
      try {
        const response = await services.inviteUser(payload);
        if (!isPlatformMutationSuccess(response)) {
          showFailure(response?.error, PLATFORM_TOAST.inviteFailure);
          return false;
        }
        closeInviteModal();
        showSuccess(PLATFORM_TOAST.inviteSuccess);
        await loadUsers(0, state.pagination.size);
        return true;
      } catch {
        showFailure(null, PLATFORM_TOAST.unexpectedError);
        return false;
      }
    },
    [
      closeInviteModal,
      loadUsers,
      services,
      showFailure,
      showSuccess,
      state.pagination.size,
    ]
  );

  const saveUserDetails = useCallback(
    async ({
      selectedUser,
      platformRoleChanged,
      customerAccessChanged,
      platformRole,
      customerAccessRows,
    }) => {
      if (!state.selectedUserId || !selectedUser) {
        return false;
      }
      if (!platformRoleChanged && !customerAccessChanged) {
        return true;
      }

      try {
        const userId = state.selectedUserId;

        if (platformRoleChanged) {
          const patchBody = {
            username: selectedUser.username,
            phoneNumber: String(selectedUser.phone ?? "").trim(),
            platformRole: platformRole || PLATFORM_USER_ACCESS_UI.defaultPlatformRole,
            accountStatus:
              normalizeCustomerAccessStatus(selectedUser.status) ||
              CUSTOMER_ACCESS_STATUS.ACTIVE,
          };
          const response = await services.patchPlatformUser(userId, patchBody);
          if (!isPlatformMutationSuccess(response)) {
            showFailure(response?.error, PLATFORM_TOAST.saveFailure);
            return false;
          }
        }

        if (customerAccessChanged) {
          const response = await services.patchPlatformUserCustomerAccess(
            userId,
            selectedUser.customerAccess,
            customerAccessRows
          );
          if (!isPlatformMutationSuccess(response)) {
            showFailure(response?.error, PLATFORM_TOAST.saveFailure);
            return false;
          }
        }

        showSuccess(PLATFORM_TOAST.saveSuccess);
        if (reloadPageIfLoggedInUser(sessionProfile, userId)) {
          return true;
        }
        await loadUsers(state.pagination.page, state.pagination.size);
        return true;
      } catch {
        showFailure(null, PLATFORM_TOAST.unexpectedError);
        return false;
      }
    },
    [
      loadUsers,
      services,
      showFailure,
      showSuccess,
      state.pagination.page,
      state.pagination.size,
      state.selectedUserId,
      sessionProfile,
    ]
  );

  const clearError = useCallback(() => {
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.ERROR,
      payload: null,
    });
  }, []);

  const onUserListAction = useCallback(async (row, action) => {
    switch (action) {
      case PLATFORM_USER_LIST_ACTION.SUSPEND: {
        try {
          const userId = row?.id;
          if (userId == null || String(userId).trim() === "") {
            showFailure(null, PLATFORM_TOAST.unexpectedError);
            break;
          }
          const result = await services.patchPlatformUserAccountStatus(userId, {
            accountStatus: CUSTOMER_ACCESS_STATUS.SUSPENDED,
            reason: "suspended by administrator",
          });
          if (!isPlatformMutationSuccess(result)) {
            showFailure(result?.error, PLATFORM_TOAST.suspendFailure);
          } else {
            showSuccess(PLATFORM_TOAST.suspendSuccess);
            if (reloadPageIfLoggedInUser(sessionProfile, userId)) {
              break;
            }
            if (String(state.selectedUserId) === String(userId)) {
              await loadUserDetails(userId);
            }
            await loadUsers(state.pagination.page, state.pagination.size);
          }
        } catch {
          showFailure(null, PLATFORM_TOAST.unexpectedError);
        }
        break;
      }
      case PLATFORM_USER_LIST_ACTION.RESEND: {
        try {
          const inviteId = row?.inviteId ?? row?.latestInvite?.inviteId;
          if (inviteId == null || String(inviteId).trim() === "") {
            showFailure(null, PLATFORM_TOAST.resendFailure);
            break;
          }
          const result = await services.resendUserInvite(inviteId);
          if (!isPlatformMutationSuccess(result)) {
            showFailure(result?.error, PLATFORM_TOAST.resendFailure);
          } else {
            showSuccess(PLATFORM_TOAST.resendSuccess);
            await loadUsers(state.pagination.page, state.pagination.size);
          }
        } catch {
          showFailure(null, PLATFORM_TOAST.unexpectedError);
        }
        break;
      }
      case PLATFORM_USER_LIST_ACTION.RESTORE: {
        try {
          const userId = row?.id;
          if (userId == null || String(userId).trim() === "") {
            showFailure(null, PLATFORM_TOAST.unexpectedError);
            break;
          }
          const result = await services.patchPlatformUserAccountStatus(userId, {
            accountStatus: CUSTOMER_ACCESS_STATUS.ACTIVE,
            reason: "restored by administrator",
          });
          if (!isPlatformMutationSuccess(result)) {
            showFailure(result?.error, PLATFORM_TOAST.restoreFailure);
          } else {
            showSuccess(PLATFORM_TOAST.restoreSuccess);
            if (reloadPageIfLoggedInUser(sessionProfile, userId)) {
              break;
            }
            if (String(state.selectedUserId) === String(userId)) {
              await loadUserDetails(userId);
            }
            await loadUsers(state.pagination.page, state.pagination.size);
          }
        } catch {
          showFailure(null, PLATFORM_TOAST.unexpectedError);
        }
        break;
      }
      default:
        break;
    }
    return { row, action };
  }, [
    loadUserDetails,
    loadUsers,
    services,
    showFailure,
    showSuccess,
    state.pagination.page,
    state.pagination.size,
    state.selectedUserId,
    sessionProfile,
  ]);

  const closeUserDetails = useCallback(() => {
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.SELECTED_USER_ID,
      payload: null,
    });
    dispatch({
      type: PLATFORM_USER_ACCESS_ACTIONS.SELECTED_USER_DETAILS,
      payload: null,
    });
  }, []);

  const refreshAfterCustomerAccessAction = useCallback(async () => {
    if (reloadPageIfLoggedInUser(sessionProfile, state.selectedUserId)) {
      return;
    }
    await loadUsers(state.pagination.page, state.pagination.size);
    if (state.selectedUserId != null && String(state.selectedUserId).trim() !== "") {
      await loadUserDetails(state.selectedUserId);
    }
  }, [
    loadUserDetails,
    loadUsers,
    state.pagination.page,
    state.pagination.size,
    state.selectedUserId,
    sessionProfile,
  ]);

  useEffect(() => {
    clearCustomerContextHeaders();
    loadUsers(0, state.pagination.size);
  }, []);

  return {
    state,
    customerRoleOptions,
    activeCustomers: activeCustomersList ?? [],
    toast,
    dismissToast,
    actions: {
      loadUsers,
      loadUserDetails,
      onListFiltersChange,
      onClearFilters,
      onRowsPerPageChange,
      onPageChange,
      openInviteModal,
      closeInviteModal,
      inviteUser,
      saveUserDetails,
      clearError,
      checkInviteEmail,
      closeUserDetails,
      onUserListAction,
      refreshAfterCustomerAccessAction,
      onCustomerUserListAction,
    },
  };
};
